import React from 'react';
import moment from 'moment';
import { ascend, flatten, isEmpty, pluck, prop, sortBy, sortWith, uniq, uniqBy } from 'ramda';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import TreatmentNotesForm from 'components/Modal/TreatmentNotesForm/TreatmentNotesForm';
import TextBoxInput from 'components/Inputs/TextBoxInput/TextBoxInput';
import numberToWords from 'utils/numberToWords';
import LabTests from 'components/Modal/LabTests/LabTests';
import PrescriptionForm from 'components/Modal/PrescriptionForm/PrescriptionForm';
import {
  ABANDONED_VISIT_TRACKER,
  buildFollowUpOpenedPayload,
  formatReferralStatus,
  getFileNameFromURL,
  isNativeApp,
  isRole,
} from 'utils/utils';
import {
  questionnairesQuery,
  labsQuery,
  followUpsQuery,
} from 'pages/PatientRecords/PatientRecords';
import { sendLabRequest, updateQuestionnaire } from 'api/api';
import { VISIT_STATUS } from 'context/visitContext';
import Modal from 'components/Modal/Modal';
import { paths } from 'routes';
import { NOTIFICATION_QUERY_KEYS } from 'utils/notificationQueries';
import Button from 'components/Button/Button';
import Skeleton from 'react-loading-skeleton';
import readAWSS3File from 'utils/readAWSS3File';
import CheckBox from 'components/Inputs/CheckBox/CheckBox';
import Icon from '../../components/Icon/Icon';
import {
  HTTPError,
  LabTest,
  LabType,
  Patient,
  Prescription,
  QuestionAnswers,
  ReferralType,
} from '../../types/types';
import { showAlert, useAppContext, usePatient } from '../../context/appContext';

export type TreatmentNote = {
  notesForPatient: string;
  notesForGuide: string;
  reviewerNotes: string | null;
  attachments: Array<string> | null;
};

type LabRequest = { labTestTypes: LabType[]; isAtHomeTest: boolean };

function TreatmentPlan() {
  const [{ user, patients }, dispatch] = useAppContext();
  const { patient } = usePatient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { visitId } = useParams<{ visitId: string }>();
  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const [showLabTestsModal, setShowLabTestsModal] = React.useState(false);
  const [showPrescriptionForm, setShowPrescriptionForm] = React.useState(false);
  const [visitAction, setVisitAction] = React.useState<'COMPLETE-VISIT' | 'REVIEW-VISIT'>();
  const [reviewerNotes, setReviewerNotes] = React.useState('');
  const [trackAbandonedVisit, setTrackAbandonedVisit] = React.useState(false);
  const [abandonedVisitType, setAbandonedVisitType] = React.useState('');

  const treatmentsQueries = useQueries({
    queries: [
      {
        ...questionnairesQuery({ id: visitId }),
        enabled: !!(isEmpty(patient?.questionnaires) && visitId),
      },
      {
        ...labsQuery({ questionnaireId: visitId }),
        enabled: !!visitId,
      },
    ],
  });

  const isLoading = treatmentsQueries.some(fndQuery => fndQuery.isLoading && fndQuery.isFetching);

  const { questionnaire, labs } = React.useMemo(() => {
    const findQuestionnaire = (
      (treatmentsQueries[0]?.data || patient?.questionnaires || []) as QuestionAnswers[]
    ).find(fndQuestionnaire => fndQuestionnaire.id === visitId);

    let findLabs = sortWith(
      [ascend(prop('createdAt') as any)],
      (treatmentsQueries[1]?.data || []) as LabTest[],
    ).map(fndLab => fndLab.types) as LabType[];

    findLabs = uniqBy(prop('id'), flatten(findLabs)); // TODO

    findLabs = sortBy(labType => !labType.isCovered, findLabs);

    return { questionnaire: findQuestionnaire, labs: findLabs };
  }, [patient?.questionnaires, treatmentsQueries, visitId]);

  const { data: followUps, isLoading: isLoadingFollowUps } = useQuery({
    ...followUpsQuery(questionnaire?.followUps),
    enabled: !!questionnaire?.followUps,
  });

  const notes = {
    notesForPatient: questionnaire?.notesForPatient || '',
    notesForGuide: questionnaire?.notesForGuide || '',
  };

  const isAssignedToPatient =
    questionnaire?.providerUserId === user?.id &&
    !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN');

  const prescriptions = questionnaire?.prescriptions || [];

  const referrals = questionnaire?.referrals || [];

  const { data: attachments, isLoading: isLoadingAttachments } = useQuery({
    queryKey: ['attachments', questionnaire?.attachments],
    queryFn: () => readAttachmentFiles(questionnaire?.attachments),
    enabled: !!questionnaire?.attachments,
  });

  const {
    mutate: addTreatmentPlanFn,
    isLoading: isAddingTreatment,
    status,
  } = useMutation(
    (variables: { questionnaireId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.questionnaireId, variables.payload),
    {
      onMutate(variables) {
        const { queryKey } = questionnairesQuery({ id: visitId });
        let oldQuestionnaire = queryClient.getQueryData<QuestionAnswers[]>(queryKey);

        const dQuestionnaire = (oldQuestionnaire || []).find(
          fndQuestionnaire => fndQuestionnaire.id === variables.questionnaireId,
        );

        if (dQuestionnaire) {
          oldQuestionnaire = [
            {
              ...dQuestionnaire,
              ...variables.payload,
            },
          ];
        }

        queryClient.setQueryData(queryKey, oldQuestionnaire);
        return oldQuestionnaire;
      },
      onSuccess: (_, variables) => {
        const { queryKey } = questionnairesQuery({ id: visitId });
        queryClient.invalidateQueries({ queryKey });

        setShowNotesModal(false);

        setShowPrescriptionForm(false);

        if (visitAction && ['COMPLETE-VISIT', 'REVIEW-VISIT'].includes(visitAction)) {
          return;
        }

        // If notes and prescriptions data was picked from patient?.questionnaires. Let dispatch
        if (patient?.questionnaires && patient?.questionnaires?.length > 0) {
          const updatePatients = patients.map(fndPatient => ({
            ...fndPatient,
            ...(fndPatient.id === patient?.id && {
              questionnaires: fndPatient.questionnaires.map(fndQuestionnaire => ({
                ...fndQuestionnaire,
                ...(fndQuestionnaire.id === questionnaire?.id && {
                  prescriptions: variables.payload.prescriptions || fndQuestionnaire.prescriptions,
                  notesForGuide: variables.payload?.notesForGuide || fndQuestionnaire.notesForGuide,
                  notesForPatient:
                    variables.payload?.notesForPatient || fndQuestionnaire.notesForPatient,
                  reviewerNotes: variables.payload?.reviewerNotes || fndQuestionnaire.reviewerNotes,
                  attachments: variables.payload?.attachments || fndQuestionnaire.attachments,
                }),
              })),
            }),
          })) satisfies Patient[];

          dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });
        }
      },
      onError: (fndError: HTTPError) => {
        console.log('HTTPError', fndError);
      },
    },
  );

  const { mutate: sendLabRequestFn, isLoading: isRequestingLab } = useMutation(
    (variables: { payload: Record<string, any> }) => sendLabRequest(variables.payload),
    {
      onSettled: (_, error) => {
        if (error) {
          console.log('HTTPError', error);
          return;
        }

        const { queryKey } = labsQuery({ questionnaireId: visitId });
        queryClient.invalidateQueries({ queryKey });
        setShowLabTestsModal(false);
      },
    },
  );

  const { mutate: markFollowUpAsReadFn } = useMutation(
    (variables: { questionnaireId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.questionnaireId, variables.payload),
    {
      onSettled: () => {
        // There will be a followUp so scroll there to view
        const element = document.getElementById(location.hash.replace('#', ''));
        element?.scrollIntoView({ behavior: 'smooth' });

        const { queryKey } = questionnairesQuery({ id: visitId });
        queryClient.invalidateQueries({ queryKey });

        NOTIFICATION_QUERY_KEYS.forEach(notificationKey =>
          queryClient.invalidateQueries({ queryKey: notificationKey }),
        );
      },
    },
  );

  const { mutate: trackAbandonedVisitFn } = useMutation(
    (variables: { questionnaireId: string; payload: Record<string, any> }) =>
      updateQuestionnaire(variables.questionnaireId, variables.payload),
    {
      onSettled: (_, error, variables) => {
        const { queryKey } = questionnairesQuery({ id: visitId });
        queryClient.invalidateQueries({ queryKey });

        // If abandoned data picked from patient?.questionnaires. Let dispatch
        if (patient?.questionnaires && patient?.questionnaires?.length > 0) {
          const updatePatients = patients.map(fndPatient => ({
            ...fndPatient,
            ...(fndPatient.id === patient?.id && {
              questionnaires: fndPatient.questionnaires.map(fndQuestionnaire => ({
                ...fndQuestionnaire,
                ...(fndQuestionnaire.id === questionnaire?.id && {
                  abandonedReason: variables.payload.abandonedReason,
                  abandonedReasonAdditionalDetails:
                    variables.payload?.abandonedReasonAdditionalDetails,
                }),
              })),
            }),
          })) satisfies Patient[];

          dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });
        }
      },
    },
  );

  React.useEffect(() => {
    if (showPrescriptionForm && !patient.photonId) {
      showAlert(dispatch, {
        show: true,
        type: 'ERROR',
        message: `Photon Integration Error! No Photon ID found for patient. Please contact our support team!`,
      });
    }
  }, [dispatch, patient.photonId, showPrescriptionForm]);

  React.useEffect(() => {
    setReviewerNotes(questionnaire?.reviewerNotes || '');
  }, [questionnaire?.reviewerNotes]);

  React.useEffect(() => {
    const abandonedTracker = ABANDONED_VISIT_TRACKER.find(
      tracker => tracker.abandonedReason === questionnaire?.abandonedReason,
    );

    if (!abandonedTracker) return;
    setTrackAbandonedVisit(true);
    setAbandonedVisitType(abandonedTracker.id);
  }, [questionnaire?.abandonedReason, questionnaire?.abandonedReasonAdditionalDetails]);

  // Guarded per visit: the mutation invalidates on settle, including on
  // failure, so without this a write that does not stick resends forever.
  const attemptedFollowUpRead = React.useRef<string | null>(null);

  React.useEffect(() => {
    const openedPayload = buildFollowUpOpenedPayload(questionnaire, visitId);

    if (!openedPayload || attemptedFollowUpRead.current === visitId) return;

    attemptedFollowUpRead.current = visitId ?? null;

    markFollowUpAsReadFn(openedPayload);
  }, [markFollowUpAsReadFn, questionnaire, visitId, location.hash]);

  const handleAddPrescriptions = (fndPrescriptions: Prescription[]) => {
    if (!visitId) return;
    addTreatmentPlanFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        prescriptions: [...fndPrescriptions, ...prescriptions],
      },
    });
  };

  const handleAddNotes = (note: Partial<TreatmentNote>) => {
    if (!visitId) return;
    addTreatmentPlanFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        ...note,
      },
    });
  };

  const handleAddLabs = (lab: LabRequest) => {
    if (!visitId) return;
    const payload = {
      isAtHomeTest: lab.isAtHomeTest,
      labTestTypeIds: uniq(pluck('id', lab.labTestTypes)),
      patientId: patient.id,
      questionnaireId: visitId,
    };
    sendLabRequestFn({ payload });
  };

  const handleCompleteVisit = () => {
    if (!visitId) return;
    setVisitAction('COMPLETE-VISIT');
    addTreatmentPlanFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        status: VISIT_STATUS.COMPLETE,
      },
    });
  };

  const handleTrackAbandonedVisit = (trackerId: string) => {
    const abandonedTracker = ABANDONED_VISIT_TRACKER.find(tracker => tracker.id === trackerId);
    if (!visitId || !trackAbandonedVisit || !abandonedTracker) return;
    setAbandonedVisitType(abandonedTracker.id);
    trackAbandonedVisitFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        abandonedReason: abandonedTracker.abandonedReason,
        abandonedReasonAdditionalDetails: abandonedTracker.abandonedReasonAdditionalDetails,
      },
    });
  };

  const handleReviewVisit = () => {
    if (!visitId || isEmpty(reviewerNotes)) return;
    setVisitAction('REVIEW-VISIT');
    addTreatmentPlanFn({
      questionnaireId: visitId,
      payload: {
        id: visitId,
        reviewerNotes,
      },
    });
  };

  const handleOpenReferral = () => {
    if (!visitId) return;
    const referralURL = `${paths.PATIENT_REFERRAL_TICKET_PATH.replace(':visitId', visitId)}${
      location.search
    }`;
    navigate(referralURL);
  };

  const handleReferralDetails = (referral: ReferralType) => {
    if (!visitId) return;
    const detailsURL = `${paths.REFERRAL_DETAILS_TICKET_PATH.replace(':visitId', visitId)}${
      location.search
    }`;
    navigate(detailsURL, { state: { referral } });
  };

  const readAttachmentFiles = async (files?: string[] | null) => {
    if (!files || files.length === 0) return [];

    return (await Promise.all(
      files.map(async file => {
        return readAWSS3File('notes', file);
      }),
    )) as string[];
  };

  const getMedicationQty = (fndPresc: Prescription) => {
    if (fndPresc.numberDispensed && fndPresc.daysSupply && fndPresc.dispenseUnit) {
      return `${fndPresc.numberDispensed} ${fndPresc.dispenseUnit.toLocaleLowerCase()} / ${
        fndPresc.daysSupply || 0
      } ${(fndPresc.daysSupply || 0) > 1 ? 'days' : 'day'}`;
    }

    if (
      (!fndPresc.dose || fndPresc.dose === '-' || fndPresc.dose === '?') &&
      (!fndPresc.frequency || fndPresc.frequency === '-' || fndPresc.frequency === '?')
    ) {
      return '-';
    }

    const dose = `${fndPresc.dose}${
      fndPresc.numberToBeTaken
        ? `, ${numberToWords(parseInt(fndPresc.numberToBeTaken, 10))} (${fndPresc.numberToBeTaken})`
        : ''
    }`;

    const frequency = `, ${fndPresc.frequency}`;

    return `${dose} ${frequency}`;
  };

  const getTextBoxInputRows = () => {
    const guideTextRow = Math.ceil(notes.notesForGuide.trim().split(/\s+/).length / 10);
    const patientTextRow = Math.ceil(notes.notesForPatient.trim().split(/\s+/).length / 10);
    return Math.min(10, Math.max(5, guideTextRow, patientTextRow));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col pt-0 pb-10 tablet:pt-16 tablet:pb-7 justify-center items-center">
        <div className="flex flex-col w-fit h-full mx-auto justify-center overflow-hidden">
          <Icon id="loader" className="w-10 h-10 text-gray-300 mx-auto" />
          <span>Loading treatment plans for {patient?.firstName || 'patient'}...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col pt-0 pb-10 tablet:pt-0">
        {/* ADD PRESCRIPTIONS */}
        <div id="add__prescriptions">
          <div
            className={`flex items-center w-full mobile:pt-0 py-8 font-medium text-[15px] text-river-blue text-left ${
              prescriptions.length > 0 ? '' : 'border-b-[1px] border-[#E0E4E7]'
            } `}
            aria-expanded="true"
            aria-controls="prescriptions"
          >
            <button
              type="button"
              onClick={() => setShowPrescriptionForm(true)}
              disabled={!isAssignedToPatient}
              className="inline-flex items-center disabled:cursor-not-allowed"
            >
              {!isAssignedToPatient ? (
                'Prescriptions'
              ) : (
                <>
                  <Icon id="addPlusSign" className="w-3 h-3 text-river-blue fill-river-blue mr-3" />
                  {prescriptions.length > 0 ? 'Add another prescription' : 'Add new prescription'}
                </>
              )}
            </button>
          </div>
          <div
            id="prescriptions"
            className={prescriptions.length > 0 ? 'block -mt-4' : 'hidden'}
            aria-labelledby="prescriptions"
          >
            <div
              className="fs-exclude px-1 pt-0 pb-10 border-b-[1px] border-[#E0E4E7] flex flex-col space-y-5
              items-stretch justify-start"
            >
              {prescriptions.map(prescription => (
                <div
                  key={prescription.prescriptionPhotonId || prescription.id}
                  className="flex flex-col w-full tablet:w-full tablet:flex-[0_0_100%] p-6 bg-white rounded-lg
                  shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] drop-shadow-sm space-y-5"
                >
                  <div className="flex flex-col tablet:flex-row w-full tablet:w-10/12">
                    <div className="flex-1">
                      <h4 className="text-[15px] text-zinc-400">Medication name</h4>
                      <p className="font-medium">{prescription.medicationName}</p>
                    </div>

                    <div className="w-full tablet:w-[20%]">
                      <h4 className="text-[15px] text-zinc-400">Date</h4>
                      <p className="font-medium">
                        {moment(prescription.createdAt).format('MM/DD/YYYY')}
                      </p>
                    </div>

                    <div className="w-full tablet:w-[15%]">
                      <h4 className="text-[15px] text-zinc-400">Refills</h4>
                      <p className="font-medium">{prescription.numberOfRefills}</p>
                    </div>

                    <div className="w-full tablet:w-[20%]">
                      <h4 className="text-[15px] text-zinc-400">Quantity</h4>
                      <p className="font-medium">{getMedicationQty(prescription)}</p>
                    </div>
                  </div>

                  <div className="w-full tablet:w-10/12">
                    <h4 className="text-[15px] text-zinc-400">Instruction</h4>
                    <p className="font-medium">{prescription.instructions}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ADD LABS */}
        <div id="add__labs">
          <div
            className={`flex items-center w-full py-8 font-medium text-[15px] text-river-blue text-left ${
              labs.length > 0 ? '' : 'border-b-[1px] border-[#E0E4E7]'
            } `}
            aria-expanded="true"
            aria-controls="labs"
          >
            <button
              type="button"
              onClick={() => setShowLabTestsModal(true)}
              disabled={!isAssignedToPatient}
              className="inline-flex items-center disabled:cursor-not-allowed"
            >
              {!isAssignedToPatient ? (
                'Labs'
              ) : (
                <>
                  <Icon id="addPlusSign" className="w-3 h-3 text-river-blue fill-river-blue mr-3" />
                  {labs.length > 0 ? 'Update lab tests' : 'Add new lab'}
                </>
              )}
            </button>
          </div>

          <div
            id="labs"
            className={labs.length > 0 ? 'block -mt-4' : 'hidden'}
            aria-labelledby="labs"
          >
            <div
              className="fs-exclude px-1 pt-0 pb-10 border-b-[1px] border-[#E0E4E7] flex flex-row flex-wrap gap-8
              items-stretch justify-start"
            >
              {labs.map(lab => (
                <div
                  key={lab.id}
                  className="flex flex-row w-full tablet:w-[31.5%] tablet:flex-[0_0_31.5%] p-6 items-center bg-white rounded-lg
                  shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] drop-shadow-sm"
                >
                  <div>
                    <h3 className="font-medium">{lab.name}</h3>
                    <p className="text-[15px]">
                      (
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(lab.price / 100)}
                      )
                    </p>
                  </div>
                  <div className="absolute bottom-5 right-5">
                    <span
                      className={`text-[11px] text-river-black bg-gray-100 rounded-full py-[8px] leading-none
                      px-4 items-center flex-shrink-0 text-ellipsis overflow-hidden whitespace-nowrap text-center 
                      group-hover:border group-hover:border-gray-300 group-hover:text-gray-500`}
                    >
                      {lab.isCovered ? 'Covered' : 'Not Covered'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ADD NOTES */}
        <div id="add__notes">
          <div
            className={`flex items-center w-full py-8 font-medium text-[15px] text-river-blue text-left
              ${
                Object.values(notes).some(fndNote => !isEmpty(fndNote))
                  ? ''
                  : 'border-b-[1px] border-[#E0E4E7]'
              }`}
            aria-expanded="true"
            aria-controls="notes"
          >
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              disabled={!isAssignedToPatient}
              className="inline-flex items-center disabled:cursor-not-allowed"
            >
              {!isAssignedToPatient ? (
                'Treatment notes'
              ) : (
                <>
                  <Icon id="addPlusSign" className="w-3 h-3 text-river-blue fill-river-blue mr-3" />
                  {Object.values(notes).some(fndNote => !isEmpty(fndNote))
                    ? 'Update treatment notes'
                    : 'Add treatment notes'}
                </>
              )}
            </button>
          </div>

          <div
            id="notes"
            className={
              Object.values(notes).some(fndNote => !isEmpty(fndNote)) ? 'block -mt-4' : 'hidden'
            }
            aria-labelledby="notes"
          >
            <div
              className={`fs-exclude px-1 pt-0 pb-7 space-y-2 flex flex-col w-full ${
                questionnaire?.status === VISIT_STATUS.UNANSWERED || !questionnaire?.reviewerNotes
                  ? ' border-b-[1px] border-[#E0E4E7]'
                  : ''
              }`}
            >
              <div className="w-full flex flex-col tablet:flex-row flex-wrap gap-14">
                <div className="flex-1">
                  <h3 className="text-sm inline-flex whitespace-pre mb-2">Provider notes</h3>
                  <TextBoxInput
                    name="notesForGuide"
                    disabled={isEmpty(notes.notesForGuide)}
                    value={notes.notesForGuide}
                    onChange={() => false}
                    rows={getTextBoxInputRows()}
                    className="!text-[15px] bg-[#FAF9F9] focus:border-gray-300 focus:ring-transparent caret-transparent"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm inline-flex whitespace-pre mb-2">Notes to patient</h3>
                  <TextBoxInput
                    name="notesForPatient"
                    value={notes?.notesForPatient || ''}
                    onChange={() => false}
                    disabled={isEmpty(notes?.notesForPatient || '')}
                    rows={getTextBoxInputRows()}
                    className="!text-[15px] bg-[#FAF9F9] focus:border-gray-300 focus:ring-transparent caret-transparent"
                  />
                </div>
              </div>

              {(questionnaire?.reviewerNotes ||
                isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) && (
                <div className="mobile:!mt-5 w-full tablet:w-[50%]">
                  <h3 className="text-sm inline-flex whitespace-pre mb-2">Reviewer Notes</h3>
                  <TextBoxInput
                    name="textToCollaborator"
                    value={reviewerNotes}
                    onChange={event => setReviewerNotes(event.target.value)}
                    disabled={Boolean(questionnaire?.reviewerNotes)}
                    rows={Math.min(
                      10,
                      Math.max(5, Math.ceil((reviewerNotes || '').trim().split(/\s+/).length / 10)),
                    )}
                    className="!text-[15px]"
                  />
                </div>
              )}

              {questionnaire?.attachments && questionnaire?.attachments.length > 0 ? (
                <div className="mobile:!mt-5 w-full">
                  {!isLoadingAttachments ? (
                    <h3 className="text-sm inline-flex whitespace-pre mb-2">Attached Files</h3>
                  ) : null}

                  <div className="flex flex-row gap-4 flex-wrap">
                    {isLoadingAttachments ? (
                      <>
                        {new Array(questionnaire?.attachments?.length)
                          .fill(null)
                          .map((_, index) => (
                            <div
                              className="flex flex-row items-center border border-[#C4C9CC]/50 rounded-full h-[40px] w-[250px] px-3 py-1"
                              key={index}
                            >
                              <div className="w-full">
                                <Skeleton
                                  width="100%"
                                  height="100%"
                                  borderRadius={50}
                                  duration={1.4}
                                />
                              </div>
                              <button
                                type="button"
                                className="w-5 h-5 bg-[#ebebeb] rounded-full flex items-center justify-center ml-2 flex-shrink-0 cursor-default"
                              >
                                <Icon id="checkmark" className="w-2 h-2 text-river-black" />
                              </button>
                            </div>
                          ))}
                      </>
                    ) : (
                      (attachments || []).map((file: string) => (
                        <div
                          className="flex flex-row items-center border border-[#C4C9CC] rounded-full px-3 py-2"
                          key={file}
                        >
                          <Link
                            to={file}
                            title={decodeURIComponent(getFileNameFromURL(file.split('?')[0]))}
                            target="_blank"
                            className="text-ellipsis overflow-hidden text-xs leading-snug whitespace-pre 
                              hover:underline hover:text-river-blue underline-offset-2"
                          >
                            {decodeURIComponent(getFileNameFromURL(file.split('?')[0]))}
                          </Link>
                          <button
                            type="button"
                            className="w-5 h-5 bg-river-blue rounded-full flex items-center justify-center ml-2 flex-shrink-0 cursor-default"
                          >
                            <Icon id="checkmark" className="text-white w-2 h-2" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ADD REFERRAL */}
        <div id="add__referrals">
          <div
            className={`flex items-center w-full py-8 font-medium text-[15px] text-river-blue text-left ${
              referrals.length > 0 ? '' : 'border-b-[1px] border-[#E0E4E7]'
            }`}
            aria-expanded="true"
            aria-controls="referrals"
          >
            <button
              type="button"
              onClick={() => handleOpenReferral()}
              disabled={isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')}
              className="inline-flex items-center disabled:cursor-not-allowed"
            >
              <Icon id="addPlusSign" className="w-3 h-3 text-river-blue fill-river-blue mr-3" />
              Referral Ticket
            </button>
          </div>

          <div
            id="referrals"
            className={referrals.length > 0 ? 'block -mt-4' : 'hidden'}
            aria-labelledby="referrals"
          >
            <div
              className="fs-exclude px-1 pt-0 pb-10 border-b-[1px] border-[#E0E4E7] flex flex-row flex-wrap gap-5
              items-stretch justify-start"
            >
              {referrals.map(referral => (
                <div
                  key={referral.id}
                  className="flex flex-row w-full tablet:w-[23%] tablet:flex-[0_0_23%] py-4 px-5 items-center bg-white rounded-lg
                  shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] drop-shadow-sm"
                >
                  <div>
                    <h3 className="font-medium">{referral.type}</h3>
                    <p className="text-zinc-400 text-[15px]">
                      {moment(referral.createdAt).format('MM/DD/YYYY')}
                    </p>
                    <div className="mt-2">
                      <span
                        className={`inline-flex items-center px-2 py-[2px] rounded-full text-[11px] font-medium ${
                          referral.status === 'COMPLETED'
                            ? 'bg-[#E4F7EE] text-[#1F7A52]'
                            : referral.status === 'IN-PROGRESS'
                            ? 'bg-[#E5F6FF] text-[#1A7FB5]'
                            : 'bg-[#F1F3F5] text-[#6B7680]'
                        }`}
                      >
                        {formatReferralStatus(referral.status)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleReferralDetails(referral)}
                      type="button"
                      className="text-river-blue text-xs mt-2 block"
                    >
                      View more
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* NO TREATMENT PLAN OFFERED */}
        <div id="no__plans__offered">
          <div className="inline-flex items-center mt-6">
            <label
              htmlFor="trackAbandonedVisit"
              className={`block pl-[15px] -indent-[15px] ${
                questionnaire?.status === VISIT_STATUS.COMPLETE ? '' : 'cursor-pointer'
              }`}
            >
              <CheckBox
                id="trackAbandonedVisit"
                name="trackAbandonedVisit"
                disabled={questionnaire?.status === VISIT_STATUS.COMPLETE}
                checked={trackAbandonedVisit}
                onChange={() => {
                  setTrackAbandonedVisit(!trackAbandonedVisit);
                }}
                className={`align-middle mr-3 ${
                  questionnaire?.status === VISIT_STATUS.COMPLETE ? '' : 'cursor-pointer'
                }`}
              />
              <span
                className={`align-middle ${
                  questionnaire?.status === VISIT_STATUS.COMPLETE ? 'opacity-50' : ''
                }`}
              >
                No Treatment Plan Offered
              </span>
            </label>
          </div>

          {trackAbandonedVisit && (
            <div className="space-y-5 mt-4">
              {ABANDONED_VISIT_TRACKER.map(tracker => (
                <button
                  id={tracker.id}
                  type="button"
                  disabled={questionnaire?.status === VISIT_STATUS.COMPLETE}
                  onClick={() => handleTrackAbandonedVisit(tracker.id)}
                  className={`block rounded-xl w-[80%] border px-5 py-3 text-left ${
                    abandonedVisitType === tracker.id
                      ? 'border-river-deep-orange'
                      : 'border-[#E0E4E7]'
                  }`}
                  aria-labelledby="labs"
                >
                  <h3>{tracker.abandonedReason}</h3>
                  <p className="text-zinc-400 text-[15px] font-light">
                    {tracker.abandonedReasonAdditionalDetails}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* FOLLOW-UP VIEW */}
        {questionnaire?.followUps ? (
          <div id="review__follow__ups">
            <div
              className="flex items-center w-full py-8 font-medium text-left"
              aria-expanded="true"
              aria-controls="labs"
            >
              Follow-Up On Visit
            </div>

            <div
              id="follow-ups"
              className="block -mt-4 rounded-xl w-[50%] border border-river-blue bg-[#F3F8FA] p-5 relative"
              aria-labelledby="labs"
            >
              {isLoadingFollowUps || !followUps ? (
                <div className="fs-exclude pb-5 flex flex-col space-y-4">
                  <div>
                    <h3 className="font-semibold">
                      <Skeleton width="40%" height={25} className="block" duration={1.4} />
                    </h3>
                    <p>
                      <Skeleton width="100%" height={50} className="block" duration={1.4} />
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold">
                      <Skeleton width="40%" height={25} className="block" duration={1.4} />
                    </h3>
                    <p>
                      <Skeleton width="100%" height={85} className="block" duration={1.4} />
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <span
                    className="absolute right-5 bg-river-black rounded-full px-4 text-white w-fit 
                    py-3 text-[13px] leading-none font-light"
                  >
                    New follow-Up
                  </span>

                  <div className="fs-exclude pb-5 flex flex-col space-y-4 mt-4">
                    {followUps.map((followUp, index) => (
                      <div
                        key={index}
                        className={`${
                          index !== followUps.length - 1
                            ? ' border-b-[1px] border-[#E0E4E7] pb-4'
                            : ''
                        }`}
                      >
                        <h3 className="font-semibold">{followUp.responseQuestion}</h3>
                        <p>{followUp.responseAnswer}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {isAssignedToPatient ? (
        questionnaire?.status === VISIT_STATUS.UNANSWERED ? (
          <button
            type="button"
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals, no-alert
              if (!confirm('Are you sure you want to close this visit?')) {
                return;
              }
              handleCompleteVisit();
            }}
            disabled={
              isEmpty(notes.notesForPatient) &&
              (!trackAbandonedVisit || isEmpty(abandonedVisitType))
            }
            className="w-fit bg-river-blue border border-river-blue text-white rounded-full px-5 py-[9px] text-sm min-w-[190px]
          hover:opacity-90 disabled:hover:opacity-100 disabled:text-gray-300/60 disabled:bg-river-blue/80
          disabled:cursor-cursor-not-allowed disabled:border-transparent"
          >
            Complete visit
          </button>
        ) : null
      ) : !questionnaire?.reviewerNotes && isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
        <Button
          label="Add Review"
          onClick={() => {
            if (!visitId || isEmpty(reviewerNotes)) return;
            // eslint-disable-next-line no-restricted-globals, no-alert
            if (!confirm('Are you sure you want to send this visit review?')) {
              return;
            }
            handleReviewVisit();
          }}
          className="w-fit bg-river-blue border border-river-blue text-white rounded-full px-5 py-[9px] text-sm min-w-[190px] hover:opacity-90"
        />
      ) : null}

      {showNotesModal && (
        <TreatmentNotesForm
          onCloseModal={() => setShowNotesModal(false)}
          initialNote={notes}
          attachments={attachments}
          isAddingNote={isAddingTreatment}
          onAddNote={handleAddNotes}
        />
      )}

      {showLabTestsModal && (
        <LabTests
          addedLabTests={labs}
          onCloseModal={() => setShowLabTestsModal(false)}
          onAddLabTests={handleAddLabs}
          isRequestingLab={isRequestingLab}
        />
      )}

      {isAssignedToPatient && patient && patient.photonId && !isNativeApp() && (
        <PrescriptionForm
          open={showPrescriptionForm}
          patientPhotonId={patient.photonId}
          onCloseModal={() => setShowPrescriptionForm(false)}
          onAddPrescriptions={handleAddPrescriptions}
          photonRedirectTo={`${window.location.origin}?photon-redirect=${encodeURIComponent(
            `${location.pathname}${location.search || ''}`,
          )}`}
        />
      )}

      {visitAction &&
        ['COMPLETE-VISIT', 'REVIEW-VISIT'].includes(visitAction) &&
        ['error', 'loading', 'success'].includes(status) && (
          <Modal show>
            <div className="rounded-xl bg-white tablet:w-[500px]">
              <div className="flex flex-col justify-center items-center px-8 pb-10 pt-6 text-center">
                <img
                  src={
                    status === 'loading'
                      ? '/assets/images/loader_min.gif'
                      : status === 'error'
                      ? '/assets/images/error_min.gif'
                      : '/assets/images/success_min.gif'
                  }
                  alt="screen loader status"
                  className="-mt-[15px]"
                  width={170}
                  height={170}
                />

                {visitAction === 'COMPLETE-VISIT' ? (
                  <p className="text-lg font-medium tracking-wider">
                    {status === 'loading'
                      ? 'Completing visit... Almost there!'
                      : status === 'error'
                      ? 'Oops! We encountered an issue completing the visit. Please retry.'
                      : "Thanks! Patient's visit completed."}
                  </p>
                ) : (
                  <p className="text-lg font-medium tracking-wider">
                    {status === 'loading'
                      ? 'Sending visit review... Almost there!'
                      : status === 'error'
                      ? 'Oops! We encountered an issue adding reviews to the visit. Please retry.'
                      : "Thanks! Patient's review added."}
                  </p>
                )}

                {['error', 'success'].includes(status) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (status === 'success') {
                        window.location.replace(paths.HOME_URL_PATH);
                        return;
                      }

                      if (status === 'error') {
                        if (isAssignedToPatient) {
                          handleCompleteVisit();
                        } else {
                          handleReviewVisit();
                        }
                      }
                    }}
                    className="w-fit bg-river-blue border border-river-blue text-white rounded-full px-5 py-[12px] text-sm mt-8 min-w-[200px] 
                    hover:opacity-90 disabled:hover:opacity-100 disabled:text-gray-300/60 disabled:bg-river-blue/80 
                    disabled:cursor-cursor-not-allowed disabled:border-transparent"
                  >
                    {status === 'error' ? 'Try again' : 'Go Home'}
                  </button>
                )}
              </div>
            </div>
          </Modal>
        )}
    </>
  );
}

export default TreatmentPlan;
